import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { Send } from "lucide-react";
import { ContactFormTurnstile, useContactFormTurnstile } from "@/app/components/ContactFormTurnstile";
import { CONTACT_FORM_PUBLIC_ERROR, postContactApi } from "@/lib/contactSubmit";
import {
  isValidDemoPhone,
  LIMITS,
  WORK_EMAIL_PATTERN,
} from "@/lib/formValidation";
import { INDUSTRY_OPTIONS, INDUSTRY_OPTION_SET } from "@/lib/industryOptions";
import { Seo } from "../components/Seo";

type DemoPlan = "essential" | "pro" | "enterprise";

type LeadValues = {
  name: string;
  email: string;
  phone: string;
  jobTitle: string;
  company: string;
  companySize: string;
  industry: string;
  plan: DemoPlan;
  useCase: string;
};

const PLAN_LABELS: Record<DemoPlan, string> = {
  essential: "Essential (Managed SaaS)",
  pro: "Pro (Managed SaaS Plus)",
  enterprise: "Enterprise (briefing / self-hosted)",
};

const PAGE_TITLES: Record<DemoPlan, string> = {
  essential: "Get started with Essential",
  pro: "Get started with Pro",
  enterprise: "Request an enterprise briefing",
};

function normalizePlan(raw: string | null): DemoPlan {
  const p = (raw ?? "").toLowerCase().trim();
  if (p === "enterprise") return "enterprise";
  if (p === "essential") return "essential";
  return "pro";
}

async function postLead(payload: Record<string, unknown>): Promise<{ ok: boolean; error?: string }> {
  return postContactApi(payload);
}

export function RequestDemo() {
  const [searchParams, setSearchParams] = useSearchParams();
  const planFromUrl = useMemo(() => normalizePlan(searchParams.get("plan")), [searchParams]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<LeadValues>({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      jobTitle: "",
      company: "",
      companySize: "",
      industry: "",
      plan: planFromUrl,
      useCase: "",
    },
  });

  const plan = watch("plan");

  useEffect(() => {
    setValue("plan", planFromUrl);
  }, [planFromUrl, setValue]);

  const { onChange: onPlanChange, ...planRegisterRest } = register("plan");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const {
    turnstileSiteKey,
    containerRef,
    turnstileReady,
    getToken,
    resetTurnstile,
    turnstileLoadError,
  } = useContactFormTurnstile();
  const successPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!success) return;
    successPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [success]);

  async function onSubmit(values: LeadValues) {
    setSubmitError(null);

    const turnstileToken = getToken();
    if (turnstileSiteKey && (!turnstileToken || turnstileToken.length === 0)) {
      setSubmitError("Complete the CAPTCHA below, then try again.");
      return;
    }

    setSubmitting(true);
    try {
      const payload: Record<string, string> = {
        type: "demo",
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        jobTitle: values.jobTitle.trim(),
        company: values.company.trim(),
        companySize: values.companySize,
        industry: values.industry.trim(),
        plan: values.plan,
        useCase: values.useCase.trim(),
      };
      if (turnstileToken) payload.turnstileToken = turnstileToken;

      const result = await postLead(payload);
      if (!result.ok) {
        if (turnstileSiteKey) resetTurnstile();
        setSubmitError(result.error ?? CONTACT_FORM_PUBLIC_ERROR);
        return;
      }
      setSuccess(true);
      if (turnstileSiteKey) resetTurnstile();
      reset({
        ...values,
        name: "",
        email: "",
        phone: "",
        jobTitle: "",
        company: "",
        companySize: "",
        industry: "",
        useCase: "",
        plan: values.plan,
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (searchParams.get("plan")?.toLowerCase().trim() === "pilot") {
    return <Navigate to="/get-started" replace />;
  }

  return (
    <>
      <Seo
        title={PAGE_TITLES[plan]}
        description="Schedule a DeskReplay demo. Tell us your plan tier, industry, and requirements and we will follow up at your work email."
        canonicalPath="/request-demo"
      />
      <div className="bg-slate-950 min-h-screen pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <p className="text-teal-400 text-xs uppercase tracking-widest font-semibold mb-3">Schedule demo</p>
          <h1 className="text-slate-100 text-4xl font-extrabold tracking-tight mb-4">{PAGE_TITLES[plan]}</h1>
          <p className="text-on-dark-muted text-lg leading-relaxed">
            Tell us who you are and how we can reach you. We respond within two business days for qualified leads.
          </p>
          <p className="text-on-dark-muted text-sm mt-4">
            General questions only? Use the{" "}
            <Link to="/contact" className="text-teal-400 hover:text-teal-300 font-medium">
              contact form
            </Link>
            .
          </p>
        </div>

        <div className="max-w-3xl">
          {success ? (
            <div
              ref={successPanelRef}
              className="scroll-mt-28 rounded-2xl border border-teal-500/25 bg-gradient-to-br from-slate-900/80 to-slate-950 p-8 sm:p-10"
            >
              <h2 className="text-slate-100 text-xl font-bold mb-3">Thank you</h2>
              <p className="text-on-dark-muted leading-relaxed mb-6">
                We received your details. Our team will follow up at the work email you provided.
              </p>
              <Link to="/pricing" className="inline-flex text-sm font-medium text-teal-400 hover:text-teal-300">
                Back to pricing
              </Link>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="rounded-2xl border border-teal-500/15 bg-gradient-to-br from-slate-900/80 to-slate-950 p-8 sm:p-10"
              noValidate
            >
              <div className="mb-6 rounded-lg border border-slate-700 bg-slate-950/50 px-4 py-3">
                <p className="text-slate-500 text-xs font-medium uppercase tracking-wide mb-1">Plan</p>
                <select
                  {...planRegisterRest}
                  onChange={(e) => {
                    void onPlanChange(e);
                    setSearchParams({ plan: e.target.value });
                  }}
                  className="w-full rounded-lg bg-slate-950 border border-slate-500 text-slate-100 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500/50"
                >
                  <option value="essential">{PLAN_LABELS.essential}</option>
                  <option value="pro">{PLAN_LABELS.pro}</option>
                  <option value="enterprise">{PLAN_LABELS.enterprise}</option>
                </select>
                <p className="text-on-dark-muted text-xs mt-2">
                  Self-serve{" "}
                  <strong className="font-medium text-slate-400">7-day free pilot</strong> (instant signup, no credit
                  card, no sales call)?{" "}
                  <Link to="/get-started" className="text-teal-400 hover:text-teal-300">
                    Start your pilot
                  </Link>
                  {" · "}
                  <Link to="/pricing#essential" className="text-teal-400 hover:text-teal-300">
                    View on pricing
                  </Link>
                </p>
              </div>

              <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide mb-4">Contact person</p>
              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <label className="flex flex-col gap-2">
                  <span className="text-slate-300 text-sm font-medium">Full name *</span>
                  <input
                    {...register("name", {
                      required: "Name is required",
                      minLength: {
                        value: LIMITS.name.min,
                        message: `Enter at least ${LIMITS.name.min} characters`,
                      },
                      maxLength: {
                        value: LIMITS.name.max,
                        message: `Name must be at most ${LIMITS.name.max} characters`,
                      },
                    })}
                    className="rounded-lg bg-slate-950 border border-slate-500 text-slate-100 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500/50"
                    placeholder="Your name"
                    autoComplete="name"
                  />
                  {errors.name && <span className="text-red-400 text-xs">{errors.name.message}</span>}
                </label>
                <label className="flex flex-col gap-2">
                  <span className="text-slate-300 text-sm font-medium">Work email *</span>
                  <input
                    type="email"
                    {...register("email", {
                      required: "Email is required",
                      maxLength: {
                        value: LIMITS.email.max,
                        message: `Email must be at most ${LIMITS.email.max} characters`,
                      },
                      pattern: {
                        value: WORK_EMAIL_PATTERN,
                        message: "Enter a valid email address",
                      },
                    })}
                    className="rounded-lg bg-slate-950 border border-slate-500 text-slate-100 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500/50"
                    placeholder="you@company.com"
                    autoComplete="email"
                  />
                  {errors.email && <span className="text-red-400 text-xs">{errors.email.message}</span>}
                </label>
              </div>

              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <label className="flex flex-col gap-2">
                  <span className="text-slate-300 text-sm font-medium">Job title *</span>
                  <input
                    {...register("jobTitle", {
                      required: "Job title is required",
                      minLength: {
                        value: LIMITS.jobTitle.min,
                        message: `Enter at least ${LIMITS.jobTitle.min} characters`,
                      },
                      maxLength: {
                        value: LIMITS.jobTitle.max,
                        message: `Job title must be at most ${LIMITS.jobTitle.max} characters`,
                      },
                    })}
                    className="rounded-lg bg-slate-950 border border-slate-500 text-slate-100 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500/50"
                    placeholder="Role or title"
                    autoComplete="organization-title"
                  />
                  {errors.jobTitle && <span className="text-red-400 text-xs">{errors.jobTitle.message}</span>}
                </label>
                <label className="flex flex-col gap-2">
                  <span className="text-slate-300 text-sm font-medium">Phone *</span>
                  <input
                    type="tel"
                    {...register("phone", {
                      required: "Phone number is required",
                      validate: (v) =>
                        isValidDemoPhone(String(v ?? "")) ? true : "Enter a valid phone number (at least 5 characters)",
                    })}
                    className="rounded-lg bg-slate-950 border border-slate-500 text-slate-100 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500/50"
                    placeholder="+1 …"
                    autoComplete="tel"
                  />
                  {errors.phone && <span className="text-red-400 text-xs">{errors.phone.message}</span>}
                </label>
              </div>

              <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide mb-4 mt-2">Company</p>
              <label className="flex flex-col gap-2 mb-5">
                <span className="text-slate-300 text-sm font-medium">Company name *</span>
                <input
                  {...register("company", {
                    required: "Company is required",
                    minLength: {
                      value: LIMITS.company.min,
                      message: `Enter at least ${LIMITS.company.min} characters`,
                    },
                    maxLength: {
                      value: LIMITS.company.max,
                      message: `Company name must be at most ${LIMITS.company.max} characters`,
                    },
                  })}
                  className="rounded-lg bg-slate-950 border border-slate-500 text-slate-100 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500/50"
                  placeholder="Legal or operating name"
                  autoComplete="organization"
                />
                {errors.company && <span className="text-red-400 text-xs">{errors.company.message}</span>}
              </label>

              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <label className="flex flex-col gap-2">
                  <span className="text-slate-300 text-sm font-medium">Company size *</span>
                  <select
                    {...register("companySize", { required: "Select company size" })}
                    className="rounded-lg bg-slate-950 border border-slate-500 text-slate-100 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500/50"
                  >
                    <option value="" disabled>
                      Select range
                    </option>
                    <option value="lt10">Under 10 employees</option>
                    <option value="10-50">10 to 50</option>
                    <option value="50-200">50 to 200</option>
                    <option value="200-1000">200 to 1,000</option>
                    <option value="1000+">1,000+</option>
                  </select>
                  {errors.companySize && (
                    <span className="text-red-400 text-xs">{errors.companySize.message}</span>
                  )}
                </label>
                <label className="flex flex-col gap-2">
                  <span className="text-slate-300 text-sm font-medium">Industry *</span>
                  <select
                    {...register("industry", {
                      required: "Select an industry",
                      validate: (v) =>
                        INDUSTRY_OPTION_SET.has(String(v ?? "").trim()) || "Select a valid industry",
                    })}
                    className="rounded-lg bg-slate-950 border border-slate-500 text-slate-100 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500/50"
                    autoComplete="organization"
                  >
                    <option value="" disabled>
                      Select industry
                    </option>
                    {INDUSTRY_OPTIONS.map((label) => (
                      <option key={label} value={label}>
                        {label}
                      </option>
                    ))}
                  </select>
                  {errors.industry && (
                    <span className="text-red-400 text-xs">{errors.industry.message}</span>
                  )}
                </label>
              </div>

              <label className="flex flex-col gap-2 mb-6">
                <span className="text-slate-300 text-sm font-medium">Your requirements *</span>
                <textarea
                  {...register("useCase", {
                    required: "Please add your requirements",
                    minLength: {
                      value: LIMITS.useCase.min,
                      message: `Please enter at least ${LIMITS.useCase.min} characters`,
                    },
                    maxLength: {
                      value: LIMITS.useCase.max,
                      message: `Requirements must be at most ${LIMITS.useCase.max} characters`,
                    },
                  })}
                  rows={4}
                  className="rounded-lg bg-slate-950 border border-slate-500 text-slate-100 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500/50 resize-y min-h-[100px]"
                  placeholder="Briefly describe what you need."
                />
                {errors.useCase && <span className="text-red-400 text-xs">{errors.useCase.message}</span>}
              </label>

              {turnstileSiteKey ? (
                <ContactFormTurnstile containerRef={containerRef} loadError={turnstileLoadError} />
              ) : null}

              {submitError && (
                <p className="text-red-400/90 text-sm mb-4" role="alert">
                  {submitError}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting || (!!turnstileSiteKey && !turnstileReady)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-500 disabled:opacity-60 disabled:pointer-events-none text-white px-8 py-3.5 rounded-xl font-semibold text-sm transition-all hover:shadow-lg hover:shadow-teal-500/25"
              >
                <Send size={16} />
                {submitting ? "Sending…" : "Submit"}
              </button>
            </form>
          )}
        </div>
      </div>
      </div>
    </>
  );
}
